import React from "react";
import { defineMessages, useIntl } from "react-intl";
import { Factor, FACTORS_TO_SHOW } from "@app/service/user";
import { RadioGroup, RadioButton } from "@natera/form";
import { Profile } from "@app/provider/profile";
import { maskEmail, maskPhone } from "@app/utils";
import { FactorsObject } from "@app/provider/mfaSetup";
import "./factorList.scss";

const messages = defineMessages({
  //factor list:
  factorListSmsTitle: {
    id: "factorListSmsTitle",
    defaultMessage: "Email or Text Message",
  },
  factorListSmsDescription: {
    id: "factorListSmsDescription",
    defaultMessage: "{phone}",
  },
  factorListEmailTitle: {
    id: "factorListEmailTitle",
    defaultMessage: "Email Only",
  },
  factorListEmailDescription: {
    id: "factorListEmailDescription",
    defaultMessage: "{email}",
  },
  factorListCallTitle: {
    id: "factorListCallTitle",
    defaultMessage: "Email or Phone Call",
  },
  factorListCallDescription: {
    id: "factorListCallDescription",
    defaultMessage: "{phone}",
  },
  //factor list for login flow:
  factorListSmsTitleLoginFlow: {
    id: "factorListSmsTitleLoginFlow",
    defaultMessage: "Text Message",
  },
  factorListSmsDescriptionLoginFlow: {
    id: "factorListSmsDescriptionLoginFlow",
    defaultMessage:
      "You will receive the security code via text message. Text or data rates may apply.",
  },
  factorListEmailTitleLoginFlow: {
    id: "factorListEmailTitleLoginFlow",
    defaultMessage: "Email",
  },
  factorListEmailDescriptionLoginFlow: {
    id: "factorListEmailDescriptionLoginFlow",
    defaultMessage: "You will receive a security code at {email}",
  },
  factorListCallTitleLoginFlow: {
    id: "factorListCallTitleLoginFlow",
    defaultMessage: "Phone Call",
  },
  factorListCallDescriptionLoginFlow: {
    id: "factorListCallDescriptionLoginFlow",
    defaultMessage: "You will receive a phone call to {phone}",
  },
  //legend:
  factorListLegend: {
    id: "factorListLegend",
    defaultMessage: "Two-Factor Authentication types",
  },
});

interface FactorList {
  [key: string]: {
    title: { id: string; defaultMessage: string };
    description: { id: string; defaultMessage: string };
  };
}

export const FACTOR_LIST: FactorList = {
  call: {
    title: messages.factorListCallTitle,
    description: messages.factorListCallDescription,
  },
  email: {
    title: messages.factorListEmailTitle,
    description: messages.factorListEmailDescription,
  },
  sms: {
    title: messages.factorListSmsTitle,
    description: messages.factorListSmsDescription,
  },
};

const FACTOR_LIST_LOGIN_FLOW: FactorList = {
  call: {
    title: messages.factorListCallTitleLoginFlow,
    description: messages.factorListCallDescriptionLoginFlow,
  },
  email: {
    title: messages.factorListEmailTitleLoginFlow,
    description: messages.factorListEmailDescriptionLoginFlow,
  },
  sms: {
    title: messages.factorListSmsTitleLoginFlow,
    description: messages.factorListSmsDescriptionLoginFlow,
  },
};

const FACTOR_DISPLAY_ORDER: FACTORS_TO_SHOW[] = [
  FACTORS_TO_SHOW.email,
  FACTORS_TO_SHOW.sms,
  FACTORS_TO_SHOW.call,
];

interface Props {
  profile?: Profile;
  factorsObject: FactorsObject;
  displayEmail?: boolean;
  setSelectedFactor: (prop: Factor | undefined) => void;
  selectedFactorType?: FACTORS_TO_SHOW;
  isLoginFlow?: boolean;
}

const FactorList: React.FunctionComponent<Props> = ({
  profile,
  factorsObject,
  displayEmail = true,
  setSelectedFactor,
  selectedFactorType,
  isLoginFlow,
}) => {
  const intl = useIntl();
  const userPhone = profile?.phone;
  const userEmail = profile?.email;

  const handleFactorSelect = (factorType: FACTORS_TO_SHOW) => {
    setSelectedFactor(factorsObject[factorType]);
  };

  const renderItem = (factorType: FACTORS_TO_SHOW) => {
    if (!displayEmail && factorType === FACTORS_TO_SHOW.email) return null;

    const factorList = isLoginFlow ? FACTOR_LIST_LOGIN_FLOW : FACTOR_LIST;
    const factorText = factorType in factorList && factorList[factorType];

    if (!factorText) return null;

    const factorData: Factor | undefined = factorsObject[factorType];
    const factorProfile = factorData?.profile;

    const emailValue = factorProfile?.email ?? userEmail;
    const phoneValue = factorProfile?.phoneNumber ?? userPhone;

    const description = intl.formatMessage(factorText.description, {
      phone: maskPhone(phoneValue),
      email: maskEmail(emailValue),
    });

    const isSelected = selectedFactorType === factorType;

    return (
      <RadioButton
        key={factorType}
        className="factor-item"
        label={
          <>
            <div className="factor-item-title">
              {intl.formatMessage(factorText.title)}
            </div>
            <div className="factor-item-desc">{description}</div>
          </>
        }
        value={factorType}
        data-testid={`factor-option-${factorType}`}
        data-selected={isSelected}
      />
    );
  };

  const renderFactors = () =>
    FACTOR_DISPLAY_ORDER.map((factorType) =>
      factorsObject[factorType] ? renderItem(factorType) : null
    );

  return (
    <div className="factor-list">
      <fieldset>
        <legend className="visually-hidden">
          {intl.formatMessage(messages.factorListLegend)}
        </legend>
        <RadioGroup
          name="factorList"
          onChange={handleFactorSelect}
          {...(selectedFactorType !== undefined
            ? { value: selectedFactorType }
            : {})}
        >
          {renderFactors()}
        </RadioGroup>
      </fieldset>
    </div>
  );
};

export default FactorList;
